import { useEffect, useId, useRef, useState } from 'react'
import { AlertTriangle, ExternalLink, Loader2, Trash2, X } from 'lucide-react'
import { fetchBlockUsage } from './chat/blockUsage'
import { deleteDef } from './defAdmin'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'

/**
 * Pop-in « Supprimer ce bloc » — un même dialogue, deux issues, tranchées par
 * Drupal et non par l'écran :
 *
 *  - PERSONNE NE S'EN SERT → une confirmation, puis la définition part.
 *  - QUELQU'UN S'EN SERT → la liste des contenus qui le référencent, et pas de
 *    bouton de suppression. Drupal n'a pas d'intégrité référentielle : supprimer
 *    quand même laisserait un trou sur des pages en ligne.
 *
 * Le verdict est demandé À L'OUVERTURE (`/api/builder/block-usage`) pour montrer
 * la liste, et RE-tranché au moment de supprimer : entre les deux, quelqu'un a
 * pu poser le bloc quelque part. Un 409 fait donc basculer la pop-in de la
 * confirmation vers la liste, sans rien détruire.
 *
 * Ce qui est listé est filtré par les DROITS de l'appelant, mais le compte qui
 * bloque ne l'est pas : d'où la ligne « dont N contenu(s) que vous ne voyez
 * pas » — sans elle, un refus paraîtrait arbitraire devant une liste vide.
 */
export const BuilderDefDeleteModal = ({ block, onClose, onDeleted }) => {
  const scrim = useScrimDismiss(onClose)
  useEscapeClose(onClose)
  const titleId = useId()
  const closeRef = useRef(null)

  const [usage, setUsage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const previous = document.activeElement
    closeRef.current?.focus()
    return () => { if (previous instanceof HTMLElement) previous.focus() }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetchBlockUsage(block.type, controller.signal, { limit: 20 }).then(result => {
      if (controller.signal.aborted) return
      // `null` = la question n'a pas pu être posée (réseau, 403). On ne suppose
      // pas « inutilisé » : le bouton reste barré et le dit.
      setUsage(result)
      setLoading(false)
    })
    return () => controller.abort()
  }, [block.type])

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)
    try {
      const result = await deleteDef(block.defNid)
      if (result.ok) {
        onDeleted(block)
        return
      }
      // Posé quelque part entre l'ouverture et le clic : on montre par quoi.
      setUsage(result.usage)
      setError(result.message)
    } catch (err) {
      setError(err.message)
    }
    setDeleting(false)
  }

  const total = usage?.totalUsage ?? 0
  const used = total > 0
  const visible = (usage?.instances ?? 0) + (usage?.dashboards ?? 0)
  const hidden = Math.max(0, total - visible)
  const pages = usage?.pages ?? []
  const dashboards = usage?.dashboardList ?? []
  const morePages = Math.max(0, (usage?.pagesTotal ?? 0) - pages.length)
  const moreDashboards = Math.max(0, (usage?.dashboards ?? 0) - dashboards.length)

  return (
    <div
      className="modal-scrim modal-scrim-over"
      {...scrim}
    >
      <div
        className="modal-panel max-w-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="flex min-w-0 items-center gap-3">
            <h2 id={titleId} className="truncate">Supprimer « {block.name} »</h2>
            <span className="truncate font-mono text-xs font-normal text-slate-400">{block.type}</span>
          </div>
          <button type="button" ref={closeRef} onClick={onClose} className="btn btn-ghost" aria-label="Fermer">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="modal-body flex-1 space-y-4" aria-live="polite" aria-busy={loading}>
          {loading && (
            <p className="flex items-center gap-2 text-slate-500">
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              Recherche des contenus qui utilisent ce bloc…
            </p>
          )}

          {!loading && !usage && (
            <p className="alert alert-warning">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Impossible de vérifier si ce bloc est utilisé. La suppression est
                bloquée tant que la vérification n'a pas abouti — fermez et
                réessayez.
              </span>
            </p>
          )}

          {!loading && usage && used && (
            <>
              <p className="alert alert-warning">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  Ce bloc est utilisé par {total} contenu{total > 1 ? 's' : ''} : il ne
                  peut pas être supprimé. Retirez-le d'abord des contenus ci-dessous.
                  {hidden > 0 && ` ${hidden} d'entre eux ne vous sont pas accessibles et ne sont pas listés.`}
                </span>
              </p>

              {pages.length > 0 && (
                <section>
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                    Pages ({usage.pagesTotal})
                  </h3>
                  <ul className="flex flex-col gap-1.5">
                    {pages.map(page => (
                      <UsageRow
                        key={page.nid}
                        label={page.title || `Page ${page.nid}`}
                        path={page.slug ? `/builder/${page.slug}` : null}
                      />
                    ))}
                  </ul>
                  {morePages > 0 && (
                    <p className="mt-1.5 text-xs text-slate-400">… et {morePages} autre{morePages > 1 ? 's' : ''}.</p>
                  )}
                </section>
              )}

              {dashboards.length > 0 && (
                <section>
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                    Tableaux de bord ({usage.dashboards})
                  </h3>
                  <ul className="flex flex-col gap-1.5">
                    {dashboards.map(dashboard => (
                      <UsageRow
                        key={dashboard.nid}
                        label={dashboard.title || `Tableau de bord ${dashboard.nid}`}
                        path={`/builder/dashboards/${dashboard.slug || dashboard.nid}?edit=1`}
                      />
                    ))}
                  </ul>
                  {moreDashboards > 0 && (
                    <p className="mt-1.5 text-xs text-slate-400">… et {moreDashboards} autre{moreDashboards > 1 ? 's' : ''}.</p>
                  )}
                </section>
              )}

              {pages.length === 0 && dashboards.length === 0 && (
                <p className="text-slate-500">
                  Aucun des contenus concernés n'est visible depuis ce compte : ce
                  sont des fragments réutilisables, ou des contenus auxquels vous
                  n'avez pas accès.
                </p>
              )}
            </>
          )}

          {!loading && usage && !used && (
            <p className="text-slate-600">
              Aucun contenu n'utilise ce bloc. Sa définition sera supprimée
              définitivement — les pages déjà en ligne ne bougent pas, mais le
              bloc ne pourra plus être posé.
            </p>
          )}

          {error && !used && <p role="alert" className="alert alert-danger">{error}</p>}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            {used ? 'Fermer' : 'Annuler'}
          </button>
          {!loading && usage && !used && (
            <button type="button" onClick={handleDelete} className="btn btn-danger" disabled={deleting}>
              {deleting
                ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" />Suppression…</>
                : <><Trash2 size={16} aria-hidden="true" />Supprimer définitivement</>}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Une ligne de la liste des contenus. Le lien ouvre un NOUVEL onglet : le
 * catalogue est ouvert au-dessus d'une page en cours d'édition, et naviguer
 * ferait perdre le travail non sauvegardé — l'ouverture est annoncée aux
 * lecteurs d'écran. Un contenu sans adresse d'édition reste listé, sans lien.
 */
const UsageRow = ({ label, path }) => (
  <li className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
    <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{label}</span>
    {path && (
      <a
        href={path}
        target="_blank"
        rel="noreferrer"
        className="btn btn-secondary btn-sm shrink-0"
      >
        <ExternalLink size={14} aria-hidden="true" />
        Ouvrir<span className="sr-only"> (nouvel onglet)</span>
      </a>
    )}
  </li>
)
